import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Check, Sparkles, Zap, Mail, Users } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const WaitlistSection = () => {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setIsSubmitting(true);
    setError("");

    try {
      await axios.post(`${BACKEND_URL}/api/waitlist`, { email });
      setIsSuccess(true);
      setEmail("");
    } catch (err) {
      if (err.response?.data?.detail === "Email already registered") {
        setError("You're already on the waitlist!");
      } else {
        setError("Something went wrong. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const benefits = [
    { icon: Sparkles, text: "Priority platform access" },
    { icon: Zap, text: "Free simulator at launch" },
    { icon: Mail, text: "AI trading assistant" },
    { icon: Users, text: "Direct line to founding team" },
  ];

  return (
    <section
      id="cta"
      className="relative py-16 md:py-24 overflow-hidden"
      data-testid="waitlist-section"
    >
      {/* Background effects */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] md:w-[600px] h-[400px] md:h-[600px] bg-gradient-to-br from-[#00D4FF]/20 to-[#8B5CF6]/20 rounded-full blur-[80px] md:blur-[100px]" />

      <div className="relative z-10 max-w-4xl mx-auto px-4 md:px-6 text-center">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
        >
          <span className="inline-block px-3 md:px-4 py-2 rounded-full glass text-xs md:text-sm font-mono text-[#00D4FF] mb-4 md:mb-6">
            EARLY ACCESS OPEN
          </span>
          <h2 className="font-['Unbounded'] text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-3 md:mb-4">
            Stop Guessing.
            <br />
            <span className="gradient-text">Start Ghosting.</span>
          </h2>
          <p className="text-base md:text-lg lg:text-xl text-[#A1A1AA] mb-8 md:mb-10 max-w-2xl mx-auto px-4">
            Join the waitlist for early access. Limited spots. No spam. Just
            alpha.
          </p>
        </motion.div>

        {/* Form */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="max-w-xl mx-auto mb-8 md:mb-12 px-2"
        >
          <AnimatePresence mode="wait">
            {isSuccess ? (
              <motion.div
                key="success"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="p-6 md:p-8 rounded-2xl md:rounded-3xl glass border border-[#10B981]/30"
                data-testid="waitlist-success"
              >
                <div className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-[#10B981]/20 flex items-center justify-center mx-auto mb-3 md:mb-4">
                  <Check size={28} className="text-[#10B981] md:w-8 md:h-8" />
                </div>
                <h3 className="font-['Unbounded'] text-xl md:text-2xl font-bold text-white mb-2">
                  You're on the list!
                </h3>
                <p className="text-[#A1A1AA] text-sm md:text-base">
                  Check your email for a welcome message from ghost@fgai.com.au
                </p>
              </motion.div>
            ) : (
              <motion.form
                key="form"
                onSubmit={handleSubmit}
                className="flex flex-col sm:flex-row gap-3 md:gap-4"
                data-testid="waitlist-form"
              >
                <div className="relative flex-1">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    className="w-full px-5 md:px-6 py-4 rounded-full glass text-white placeholder-[#A1A1AA] focus:outline-none focus:ring-2 focus:ring-[#00D4FF]/50 ice-hover text-base"
                    required
                    data-testid="waitlist-email-input"
                  />
                </div>
                <motion.button
                  type="submit"
                  disabled={isSubmitting}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="px-6 md:px-8 py-4 rounded-full bg-gradient-to-r from-[#00D4FF] to-[#8B5CF6] text-white font-bold flex items-center justify-center gap-2 hover:shadow-[0_0_40px_rgba(0,212,255,0.5)] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation active:scale-95 text-base"
                  data-testid="waitlist-submit-button"
                >
                  {isSubmitting ? (
                    <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>
                      Get Early Access
                      <ArrowRight size={20} />
                    </>
                  )}
                </motion.button>
              </motion.form>
            )}
          </AnimatePresence>

          {error && (
            <p className="mt-4 text-[#EF4444] text-sm" data-testid="waitlist-error">
              {error}
            </p>
          )}
        </motion.div>

        {/* Benefits - Responsive grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="grid grid-cols-2 md:flex md:flex-wrap justify-center gap-4 md:gap-6 px-2"
        >
          {benefits.map((benefit, index) => (
            <div
              key={benefit.text}
              className="flex items-center gap-2 text-xs md:text-sm text-[#A1A1AA]"
            >
              <benefit.icon size={14} className="text-[#00D4FF] md:w-4 md:h-4 flex-shrink-0" />
              <span>{benefit.text}</span>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default WaitlistSection;
